<template>
    <div class="payouts-page">
        <HeaderBar section-label="Approval" />
        <div id="full-container">
        <div class="loading-screen" v-if="!callReturned">
            <span class="loading-text">Plan Loading....</span>
            <div class="lds-dual-ring"></div>
        </div>
        <div class="error-window" v-else-if="gotLoad404">
            <span class="error-title">Plan not found</span>
            <span class="error-subtitle">Couldn't find a plan with code {{ planKey }} in database. If you're sure that code is correct, Carter can help get everything sorted out!</span>
        </div>
        <div class="error-window" v-else-if="gotLoad500">
            <span class="error-title">Server Error</span>
            <span class="error-subtitle">The server encountered an error trying to retrieve that plan. Try refreshing the page- if that doesn't work, please tell Carter about the error!</span>
        </div>
        <template v-else>
            <h1 id="top-text">Membership Fee Schedule</h1>
            <div id="window">
            <div class="left-side">
                <div id="top-row">
                    <h2 class="top-description">View and manage your practice payouts here.</h2>
                </div>
                <div class="info-message" v-if="hasManualPayouts">
                    <i class="pi pi-info-circle"></i>
                    <span>Payouts displayed in <span class="blue-text">blue</span> have been manually set and are not changed by adjusting benefit prices in Plan Pricing.</span>
                </div>
                <div class="table-container">
                        <div class="header-row-with-space">
                            <div class="header-row">
                                <div class="essential-first-column header">Benefits Included in Plans</div>
                                <div class="essential-other-column header" style="padding-left: 1%;">Code</div>
                                <div class="essential-other-column header" style="padding-left: 1%;">UCR</div>
                                <div class="essential-other-column header" style="padding-left: 1%;">Membership Fee</div>
                            </div>
                            <div class="header-spacer"></div>
                        </div>
                        <div class="table-with-buttons">
                            <table>
                                <tbody>
                                    <tr v-for="benefit in includedBenefits" :key="benefit.id">
                                        <td class="essential-first-column">{{ benefit.name }}</td>
                                        <td class="essential-other-column">{{ benefit.code }}</td>
                                        <td class="essential-other-column">
                                            {{ benefit.UCR }}
                                        </td>
                                        <td class="essential-other-column">
                                            <input v-model="benefit.payout"
                                                class="standard-text-input grow-medium"
                                                :class="{'payout-manual': benefit.payoutSetManually}"
                                                type="text"
                                                @input="validatePayout(benefit, $event.target.value)"/>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <div class="reset-buttons-column">
                                <div v-for="benefit in includedBenefits" :key="'reset-' + benefit.id" class="reset-button-row">
                                    <button v-if="benefit.payoutSetManually"
                                        class="reset-button hover-grow"
                                        @click="resetPayout(benefit)">
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div class="header-row-with-space">
                            <div class="header-row">
                                <div class="essential-first-column header">Other Benefits</div>
                            </div>
                            <div class="header-spacer"></div>
                        </div>
                        <div class="table-with-buttons">
                            <table>
                                <tbody>
                                    <tr v-for="benefit in otherBenefits" :key="benefit.id">
                                        <td class="essential-first-column">{{ benefit.name }}</td>
                                        <td class="essential-other-column">{{ benefit.code }}</td>
                                        <td class="essential-other-column">
                                            {{ benefit.UCR }}
                                        </td>
                                        <td class="essential-other-column">
                                            <input v-model="benefit.payout"
                                                class="standard-text-input grow-medium"
                                                :class="{'payout-manual': benefit.payoutSetManually}"
                                                type="text"
                                                @input="validatePayout(benefit, $event.target.value)"/>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <div class="reset-buttons-column">
                                <div v-for="benefit in otherBenefits" :key="'reset-' + benefit.id" class="reset-button-row">
                                    <button v-if="benefit.payoutSetManually" 
                                        class="reset-button hover-grow" 
                                        @click="resetPayout(benefit)">
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            <div class="btn-container">
                <button class="add-btn hover-grow" @click="SaveChanges()">
                    {{ btnText }}
                </button>
            </div>
            <div class="submit-success" v-if="showSaveSuccess">
                Changes saved successfully!
            </div>
            <div class="response-404" v-if="update404">
                We apologize, but we can't find the plan you're working on. If you have urgent changes, please contact our data team at <a href="mailto:developer@thedentalmenu.com" style="color:var(--color-bright-blue)">developer@thedentalmenu.com</a> and we'll take care of them for you!
            </div>
            <div class="response-404" v-if="received500">
                We apologize, but we encountered an error while updating your plan. If you have urgent changes, please contact our data team at <a href="mailto:developer@thedentalmenu.com" style="color:var(--color-bright-blue)">developer@thedentalmenu.com</a> and we'll take care of them for you!
            </div>
        </div>
        
        </template>
        </div>
    </div>
</template>

<script>
import HeaderBar from '../Onboarding/components/HeaderBar.vue';
import { mapActions } from 'vuex';
import { InputToInteger, GetPlanFromDB, UpdatePlanDataInDB } from '../Onboarding/utils/functions.js';

export default {
    name: 'Payouts',
    components: {
        HeaderBar,
    },
    props: {
        planKey: String,
    },
    data() {
        return {
            callReturned: false,
            gotLoad404: false,
            gotLoad500: false,
            localPlan: null,
            planID: 0,
            btnText: "Save Changes",
            showSaveSuccess: false,
            update404: false,
            received500: false,
            isSaving: false,
            excludedBenefits: [18, 19, 22, 23, 24]
        }
    },
    methods: {
        ...mapActions(['updateBenefitByIDStore', 'setEverything']),
        validatePayout(benefit, val) {
            let result = InputToInteger(val);
            
            // Safeguard: Handle NaN, null, or undefined from InputToInteger
            if (isNaN(result) || result === null || result === undefined) {
                // Fall back to current payout if valid, otherwise use price, otherwise 0
                if (benefit.payout !== null && benefit.payout !== undefined && !isNaN(benefit.payout)) {
                    result = benefit.payout;
                } else if (benefit.price !== null && benefit.price !== undefined && !isNaN(benefit.price)) {
                    result = benefit.price;
                } else {
                    result = 0;
                }
            }
            
            benefit.payout = result;
            // If the manually entered value matches the benefit's price, treat it as not manually set
            if (result === benefit.price) {
                benefit.payoutSetManually = false;
            } else {
                benefit.payoutSetManually = true;
            }
            this.updateBenefitByIDStore(benefit);
        },
        resetPayout(benefit) {
            if (benefit.price === undefined || benefit.price === null) {
                benefit.price = 0;
            }
            else {
                benefit.payout = benefit.price;
            }
            benefit.payoutSetManually = false;
            this.updateBenefitByIDStore(benefit);
        },
        async LoadPlan() {
            // console.log("Loading plan");
            let returnData = await GetPlanFromDB(this.planKey);
            // console.log("returnData = ", returnData);
            if (returnData === 404) {
                this.gotLoad404 = true;
                this.callReturned = true;
                return;
            }
            else if (returnData === 500) {
                this.callReturned = true;
                this.gotLoad500 = true;
                return;
            }
            this.localPlan = JSON.parse(returnData.plans);
            this.planID = returnData.id;
            this.setEverything(this.localPlan);
            this.callReturned = true;
        },
        async SaveChanges() {
            if(this.isSaving) {
                return;
            }
            this.isSaving = true;
            this.btnText = "Saving...";
            this.showSaveSuccess = false;
            this.update404 = false;
            this.received500 = false;
            
            const result = await UpdatePlanDataInDB(this.planID, this.$store.state);
            
            if(result.status === 404) {
                this.update404 = true;
                this.btnText = "Save Changes";
                this.isSaving = false;
                return;
            }
            else if(result.status === 500) {
                this.received500 = true;
                this.btnText = "Save Changes";
                this.isSaving = false;
                return;
            }
            else {
                this.showSaveSuccess = true;
                this.btnText = "Changes Saved";
                this.isSaving = false;
                setTimeout(() => {
                    this.btnText = "Save Changes";
                    this.showSaveSuccess = false;
                }, 3000);
            }
        }
    },
    mounted() {
        this.LoadPlan();
    },
    computed: {
        includedBenefits() {
            let includedBenefits = [];
            this.localPlan.benefits.forEach((benefit) => {
                // console.log("Processing benefit ", benefit, " in includedBenefits")
                if(benefit.planTypes.length > 0) {
                    if(this.excludedBenefits.includes(benefit.id)) {
                        // console.log("Benefit ", benefit, " is not excluded")
                        return;
                    }
                    else {
                        includedBenefits.push(benefit);
                    }
                }
            })
            includedBenefits.sort((a, b) => {
                const codeA = parseInt(a.code.substring(1), 10);
                const codeB = parseInt(b.code.substring(1), 10);
                return codeA - codeB;
            });
            return includedBenefits;
        },
        otherBenefits() {
            let otherBenefits = [];
            this.localPlan.benefits.forEach((benefit) => {
                // console.log("Processing benefit ", benefit, " in otherBenefits")
                if(benefit.planTypes.length === 0) {
                    if(this.excludedBenefits.includes(benefit.id)) {
                        return;
                    }
                    else {
                        otherBenefits.push(benefit);
                    }
                }
            })
            otherBenefits.sort((a, b) => {
                const codeA = parseInt(a.code.substring(1), 10);
                const codeB = parseInt(b.code.substring(1), 10);
                return codeA - codeB;
            });
            return otherBenefits;
        },
        essentialCodes() {
            let codes = this.$store.state.essentialCodes;
            if(this.$store.state.isPediatric) {
                codes.splice(4,1);
            }
            return codes;
        },
        hasManualPayouts() {
            return this.$store.state.benefits.some(benefit => benefit.payoutSetManually === true);
        }
    }
}
</script>

<style scoped>
.payouts-page {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
}

#full-container {
    width: 100%;
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-direction: column;
    margin: 0;
    background-color: var(--color-teal-background);
    padding: 2% 0;
    box-sizing: border-box;
}

#top-text {
    margin: 0;
    margin-top: 1%;
    margin-bottom: 1%;
    color: var(--font-color-teal);
}

#window {
    width: 85%;
    min-height: 600px;
    background-color: white;
    border-radius: 20px;
    padding-bottom: 2em;
}

.left-side {
    display: flex;
    flex: 3;
    flex-direction: column;
    align-items: center;
}

.table-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 70%;
    margin: 20px;
    overflow-x: auto;
    margin-top: 0;
}

.header-row-with-space {
    display: flex;
    align-items: center;
    gap: 10px;
}

.header-row {
    display: flex;
    margin-left: 1%;
    font-weight: 700;
    align-items: center;
    font-size: 1.1em;
    flex: 1;
}

.header-spacer {
    min-width: 80px;
}

table {
    width: 100%;
    border-collapse: collapse;
}

table, th, td {
    border: 1px solid var(--color-dark-blue);
}

th, td {
    text-align: left;
    padding: 8px;
}

th {
    background-color: white;
}

tr:nth-child(even) {
    background-color: var(--color-light-gray);
}

input[type="text"] {
    width: 100%;
    padding: 6px 10px;
    margin: 4px 0;
    box-sizing: border-box;
}

.first-column {
    width: 50%;
    padding-top: 0;
    padding-bottom: 0;
}

.header {
    padding-top: 0.5em;
    padding-bottom: 0.5em;
    text-align: center;
}

.second-column {
    width: 25%;
    padding-top: 0;
    padding-bottom: 0;
}

.third-column {
    width: 25%;
    padding-top: 0;
    padding-bottom: 0;
}

.essential-first-column {
    width: 40%;
    padding-top: 0;
    padding-bottom: 0;
}

.essential-other-column {
    width: 20%;
    padding-top: 0;
    padding-bottom: 0;
}

.table-with-buttons {
    display: flex;
    align-items: flex-start;
    gap: 10px;
}

.table-with-buttons table {
    flex: 1;
}

.reset-buttons-column {
    display: flex;
    flex-direction: column;
    min-width: 80px;
}

.reset-button-row {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 41px;
    box-sizing: border-box;
}

.reset-button {
    background-color: white;
    border: 2px solid var(--color-light-blue);
    border-radius: 20px;
    color: var(--color-bright-blue);
    font-size: 0.85em;
    font-weight: 600;
    padding: 0.4em 0.8em;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
}

.reset-button:hover {
    border-color: var(--color-dark-blue);
    background-color: var(--color-light-blue);
    transform: scale(1.05);
}

.reset-button:focus {
    outline: none;
    border-color: var(--color-dark-blue);
}

.payout-manual {
    color: var(--color-bright-blue);
    font-weight: 600;
}

.info-message {
    display: flex;
    align-items: center;
    gap: 0.5em;
    background-color: #e3f2fd;
    border: 2px solid var(--color-light-blue);
    border-radius: 10px;
    padding: 0.75em 1em;
    margin: 1em 2em;
    color: var(--color-dark-blue);
    font-size: 1em;
}

.info-message i {
    color: var(--color-bright-blue);
    font-size: 1.3em;
}

.blue-text {
    color: var(--color-bright-blue);
    font-weight: 700;
}

#top-row {
    display: flex;
    width: 100%;
    justify-content: space-between;
}

#top-row h2 {
    margin-left: 2em;
    margin-right: 2em;
    font-size: 1.3em;
}

.delete-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.btn-container {
    display: flex;
    justify-content: center;
    width: 97%;
    margin-bottom: 1em;
    margin-top: 1em;
}

.add-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px solid var(--color-light-blue);
    border-radius: 40px;
    height: 4em;
    width: 40%;
    background-color: white;
    color: var(--color-bright-blue);
    font-size: 1.5em;
    font-weight: 600;
    padding-right: 0.7em;
    padding-left: 0.7em;
    cursor: pointer;
    max-height: 55px;
    margin: 0.5em;
    min-width: 200px;
}

.add-btn:hover {
    transform: scale(1.05);
}

/* Add transition for grow effects if they're used in your project */
.grow-medium {
    transition: transform 0.2s ease;
}

.grow-medium:hover {
    transform: scale(1.05);
}

.hover-grow {
    transition: transform 0.2s ease;
}

.hover-grow:hover {
    transform: scale(1.05);
}

.delete {
    color: var(--color-medium-red);
    font-size: 1.2em;
}

/* Loading and Error States */
.loading-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 85%;
    min-height: 400px;
    background-color: white;
    border-radius: 20px;
    padding: 2em;
}

.loading-text {
    color: var(--font-color-teal);
    font-size: 2em;
    margin-bottom: 1em;
}

.lds-dual-ring {
    display: inline-block;
    width: 80px;
    height: 80px;
}

.lds-dual-ring:after {
    content: " ";
    display: block;
    width: 64px;
    height: 64px;
    margin: 8px;
    border-radius: 50%;
    border: 6px solid transparent;
    border-color: var(--color-dark-blue) transparent var(--color-dark-blue) transparent;
    animation: lds-dual-ring 1.2s linear infinite;
}

@keyframes lds-dual-ring {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

.error-window {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 85%;
    min-height: 400px;
    background-color: white;
    border-radius: 20px;
    padding: 2em;
    text-align: center;
}

.error-title {
    color: var(--font-color-teal);
    font-size: 2em;
    margin-bottom: 1em;
    font-weight: 700;
}

.error-subtitle {
    color: var(--color-dark-blue);
    font-size: 1.2em;
    max-width: 600px;
}

/* Save Messages */
.submit-success {
    margin-top: 0.5em;
    margin-bottom: 1em;
    text-align: center;
    color: var(--color-bright-blue);
    font-size: 1.5em;
    font-weight: 700;
}

.response-404 {
    width: 80%;
    text-align: center;
    color: var(--color-medium-red);
    margin-top: 1em;
    margin-bottom: 1em;
    font-size: 1.1em;
}
</style>